import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, LayoutDashboard, ArrowRight, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { BoardListSkeleton } from '../components/LoadingSkeleton';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';

export default function BoardList() {
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { authedFetch, isEditor, isAdmin } = useAuth();

  const fetchBoards = async () => {
    try {
      const data = await authedFetch('/api/boards');
      setBoards(data);
    } catch (e) {
      toast.error('Failed to load boards');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBoards(); }, []);

  const handleCreate = async () => {
    try {
      const board = await authedFetch('/api/boards', {
        method: 'POST',
        body: JSON.stringify({ name: 'New Board' }),
      });
      window.dispatchEvent(new Event('boards-changed'));
      navigate(`/boards/${board.id}`);
      toast.success('Board created');
    } catch (e) {
      toast.error(e.message || 'Failed to create board');
    }
  };

  const handleDelete = async (boardId, boardName) => {
    if (!window.confirm(`Delete "${boardName}"? This will also delete all projects in this board.`)) return;
    try {
      await authedFetch(`/api/boards/${boardId}`, { method: 'DELETE' });
      window.dispatchEvent(new Event('boards-changed'));
      fetchBoards();
      toast.success('Board deleted');
    } catch (e) {
      toast.error(e.message || 'Failed to delete board');
    }
  };

  if (loading) {
    return <BoardListSkeleton />;
  }

  return (
    <div className="px-6 py-8 max-w-5xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Boards</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {boards.length} board{boards.length !== 1 ? 's' : ''}
          </p>
        </div>
        {isEditor && (
          <Button onClick={handleCreate} className="gap-1.5" data-testid="board-create-button-main">
            <Plus className="h-4 w-4" />
            New Board
          </Button>
        )}
      </div>

      {boards.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <LayoutDashboard className="h-12 w-12 text-muted-foreground/40 mb-4" />
            <h2 className="font-heading text-lg font-semibold mb-2">No boards yet</h2>
            <p className="text-sm text-muted-foreground mb-4">Create your first board to start tracking projects</p>
            {isEditor && (
              <Button onClick={handleCreate} className="gap-1.5">
                <Plus className="h-4 w-4" />
                Create Board
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {boards.map((b) => (
            <div key={b.id} data-testid={`board-card-${b.id}`}>
              <Card className="h-full hover:shadow-md transition-shadow cursor-pointer group">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <Link to={`/boards/${b.id}`} className="flex-1 min-w-0">
                      <h3 className="font-heading font-semibold text-base truncate pr-2">{b.name}</h3>
                    </Link>
                    <div className="flex items-center gap-1 shrink-0">
                      {isAdmin && (
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDelete(b.id, b.name); }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                          data-testid={`delete-board-${b.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <Link to={`/boards/${b.id}`}>
                        <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </Link>
                    </div>
                  </div>
                  <Link to={`/boards/${b.id}`}>
                    {b.summary && (
                      <div className="flex gap-3 mt-4 text-xs">
                        <span className="text-muted-foreground">{b.summary.total} projects</span>
                        {b.summary.green > 0 && <span className="text-[hsl(var(--rag-green))]">{b.summary.green} green</span>}
                        {b.summary.amber > 0 && <span className="text-[hsl(var(--rag-amber))]">{b.summary.amber} amber</span>}
                        {b.summary.red > 0 && <span className="text-[hsl(var(--rag-red))]">{b.summary.red} red</span>}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground mt-3">
                      Updated {new Date(b.last_updated).toLocaleDateString()}
                    </p>
                  </Link>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
